import { __read, __spreadArray } from "tslib";
import React, { useState, useRef, useCallback, useMemo, memo, } from 'react';
import Button from '@mercell/button-react';
import { Close24, Search24 } from '@carbon/icons-react';
import { Input, InputContainer, InputLeftElement, InputRightElement, } from '@mercell/input-react';
import { VariableSizeList as List } from 'react-window';
import stringify from 'fast-safe-stringify';
import cx from 'classnames';
import { prepareCodesToRender, useDeepCompareMemo } from './utlis';
import { CPVRow } from './CPVRow';
var DefaultItemHeight = 35;
var DefaultPickerHeight = 400;
var overscanCount = 10;
export var CPVModalPicker = memo(function (_a) {
    var noCodesFoundText = _a.noCodesFoundText, selectedCodesText = _a.selectedCodesText, title = _a.title, onCloseModalCallback = _a.onCloseModalCallback, cpvCodes = _a.cpvCodes, selectedCPVs = _a.selectedCPVs, ariaCollapseChevronLabel = _a.ariaCollapseChevronLabel, ariaExpandChevronLabel = _a.ariaExpandChevronLabel, cpvSearchPlaceholder = _a.cpvSearchPlaceholder, ariaLabelClearSearch = _a.ariaLabelClearSearch, onCPVListChange = _a.onCPVListChange, _b = _a.allowToSelectOnlyOne, allowToSelectOnlyOne = _b === void 0 ? false : _b, _c = _a.disabledCPVs, disabledCPVs = _c === void 0 ? [] : _c, cancelText = _a.cancelText, saveText = _a.saveText;
    var list = useRef(null);
    var outerRef = useRef(null);
    var _d = __read(useState([]), 2), selectedParentToExpand = _d[0], setSelectedParentToExpand = _d[1];
    var _e = __read(useState(selectedCPVs), 2), selectedCodes = _e[0], setSelectedCodes = _e[1];
    var _f = __read(useState(''), 2), searchValue = _f[0], setSearchValue = _f[1];
    var toggleSelectedCodesCheckbox = useCallback(function (event) {
        var code = event.currentTarget.dataset.code;
        if (code) {
            setSelectedCodes(function (currentSelectedCodes) {
                if (currentSelectedCodes.includes(code)) {
                    return currentSelectedCodes
                        .filter(function (selectedCode) { return selectedCode !== code; })
                        .sort(function (a, b) { return a.localeCompare(b); });
                }
                return __spreadArray(__spreadArray([], __read(currentSelectedCodes), false), [code], false).sort(function (a, b) {
                    return a.localeCompare(b);
                });
            });
        }
    }, []);
    var toggleSelectedCodesRadio = useCallback(function (event) {
        var code = event.currentTarget.dataset.code;
        if (code) {
            setSelectedCodes([code]);
        }
    }, []);
    var onSave = function () {
        onCPVListChange(selectedCodes);
        onCloseModalCallback();
    };
    var toggleParentExpand = useCallback(function (event) {
        var code = event.currentTarget.dataset.code;
        if (code) {
            setSelectedParentToExpand(function (currSelected) {
                if (currSelected.includes(code)) {
                    return currSelected.filter(function (selectedCode) { return selectedCode !== code; });
                }
                return __spreadArray(__spreadArray([], __read(currSelected), false), [code], false);
            });
        }
    }, []);
    var clearSearchValue = function () { return setSearchValue(''); };
    var onChangeSearchValue = function (event) {
        var value = event.currentTarget.value;
        setSearchValue(value.toLowerCase());
    };
    var codesWithParents = new Set();
    var codesToRender = prepareCodesToRender(cpvCodes, searchValue, codesWithParents, selectedParentToExpand);
    var memoizedItemData = useMemo(function () { return ({
        codesToRender: codesToRender,
        cpvCodes: cpvCodes,
        selectedCodes: selectedCodes,
        searchValue: searchValue,
        toggleParentExpand: toggleParentExpand,
        toggleSelectedCodesCheckbox: toggleSelectedCodesCheckbox,
        toggleSelectedCodesRadio: toggleSelectedCodesRadio,
        selectedParentToExpand: selectedParentToExpand,
        ariaCollapseChevronLabel: ariaCollapseChevronLabel,
        ariaExpandChevronLabel: ariaExpandChevronLabel,
        allowToSelectOnlyOne: allowToSelectOnlyOne,
        disabledCPVs: disabledCPVs,
    }); }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useDeepCompareMemo([
        codesToRender,
        cpvCodes,
        selectedCodes,
        searchValue,
        toggleParentExpand,
        toggleSelectedCodesCheckbox,
        toggleSelectedCodesRadio,
        selectedParentToExpand,
        ariaCollapseChevronLabel,
        ariaExpandChevronLabel,
        allowToSelectOnlyOne,
        disabledCPVs,
    ]));
    var itemSize = useCallback(function () { return DefaultItemHeight; }, []);
    return (React.createElement(React.Fragment, null,
        React.createElement("h2", { className: "mb-4" }, title),
        React.createElement("div", { className: "grid grid-cols-[3fr_1fr] items-center gap-4" },
            React.createElement(InputContainer, null,
                React.createElement(InputLeftElement, { className: "" },
                    React.createElement(Search24, null)),
                React.createElement(Input, { placeholder: cpvSearchPlaceholder, type: "text", value: searchValue, onChange: onChangeSearchValue, className: "pl-10" }),
                React.createElement(InputRightElement, { className: cx('flex', !searchValue && 'hidden') },
                    React.createElement("button", { type: "button", "aria-label": ariaLabelClearSearch, onClick: clearSearchValue },
                        React.createElement(Close24, null)))),
            React.createElement("div", { className: "flex items-center h-full justify-center bg-athens rounded-default" },
                React.createElement("span", { className: "text-caption mr-2" },
                    selectedCodesText,
                    ":"),
                React.createElement("span", { className: "text-caption font-bold" }, selectedCodes.length))),
        codesToRender.length ? (React.createElement("div", { className: "h-[400px] my-[10px]" },
            React.createElement(List, { ref: list, outerRef: outerRef, estimatedItemSize: DefaultItemHeight, height: DefaultPickerHeight, width: "100%", overscanCount: overscanCount, itemCount: codesToRender.length, itemData: memoizedItemData, itemSize: itemSize }, CPVRow))) : (React.createElement("div", { className: "h-[400px] my-[10px] flex items-center justify-center" },
            React.createElement("p", { className: "text-dove" }, noCodesFoundText))),
        React.createElement("div", { className: "flex justify-end" },
            React.createElement(Button, { scheme: "secondary", className: "mr-2", onClick: onCloseModalCallback }, cancelText),
            React.createElement(Button, { scheme: "primary", onClick: onSave }, saveText))));
}, function (prevProps, nextProps) { return stringify(prevProps) === stringify(nextProps); });
